import { useEffect, useMemo, type ReactNode } from 'react';
import { Checkbox, InputNumber, Radio, Slider } from 'antd';

import type { AxisConfiguration, SortOrder } from 'components/molecules/PackArchitecture/axisConfiguration.utils';
import { formatShowTopStatsLine } from 'components/molecules/PackArchitecture/axisConfiguration.utils';
import {
    handleKpiIntegerInputKeyDown,
    KPI_INTEGER_INPUT_PROPS,
    parseKpiIntegerInput,
} from 'components/molecules/PackArchitecture/kpiRangeSlider.utils';
import { useAxisShowTopInput } from 'components/molecules/PackArchitecture/useAxisShowTopInput';
import type { AxisGroupByControlsMeta } from 'components/molecules/PackArchitecture/axisGroupByControlsMeta.types';
import {
    PPA_AXIS_CONFIG_GROUP_REST_CHECKBOX,
    PPA_AXIS_CONFIG_ORDER_ASC,
    PPA_AXIS_CONFIG_ORDER_DESC,
    PPA_AXIS_CONFIG_SHOW_TOP_INPUT,
    ppaAxisConfigSortByTestId,
} from 'constants/ppaTestIds';

type OrderToggleProps = {
    orderOptions: Array<{ id: SortOrder; label: string }>;
    value: SortOrder;
    onChange: (value: SortOrder) => void;
};

const OrderToggle = ({ orderOptions, value, onChange }: OrderToggleProps) => {
    const ascOption = orderOptions.find((option) => option.id === 'asc') ?? orderOptions[0];
    const descOption = orderOptions.find((option) => option.id === 'desc') ?? orderOptions[1];

    if (!ascOption || !descOption) return null;

    return (
        <div className="inline-flex h-8 overflow-hidden rounded-lg border border-borderGreyAl1">
            <button
                type="button"
                aria-pressed={value === 'asc'}
                className={`h-8 w-[106px] text-sm font-medium ${
                    value === 'asc' ? 'bg-bgBackground text-purpleFont' : 'bg-white text-toolbarLabelGrey'
                }`}
                onClick={() => onChange('asc')}
                data-testid={PPA_AXIS_CONFIG_ORDER_ASC}
            >
                {ascOption.label}
            </button>
            <div className="w-px self-stretch bg-borderGreyAl1" />
            <button
                type="button"
                aria-pressed={value === 'desc'}
                className={`h-8 w-[106px] text-sm font-medium ${
                    value === 'desc' ? 'bg-bgBackground text-purpleFont' : 'bg-white text-toolbarLabelGrey'
                }`}
                onClick={() => onChange('desc')}
                data-testid={PPA_AXIS_CONFIG_ORDER_DESC}
            >
                {descOption.label}
            </button>
        </div>
    );
};

type ConfigFieldRowProps = {
    label: string;
    children: ReactNode;
};

const ConfigFieldRow = ({ label, children }: ConfigFieldRowProps) => (
    <div className="flex items-start gap-3">
        <p className="m-0 w-[80px] shrink-0 text-base font-medium leading-6 text-blackTitle">{label}</p>
        <div className="min-w-0 flex-1">{children}</div>
    </div>
);

const SectionDivider = () => <div className="border-t border-backgroundLightGrey" />;

type AxisConfigurationModalConfigurationControlsProps = {
    config: AxisConfiguration;
    controlsMeta: AxisGroupByControlsMeta;
    totalCount: number;
    valueSalesPercent: number;
    volumeSalesPercent: number;
    isPreviewLoading?: boolean;
    hideGroupRestAsOther?: boolean;
    /** Hides the "X/Y • value% Value Sales • volume% Volume Sales" line - the
     * bucket-level API data backing it doesn't carry volume_sales for the
     * Y-axis attribute-bucket case (e.g. Pack_Range), so it's hidden there
     * rather than shown with an incorrect volume share. */
    hideShowTopStats?: boolean;
    onChange: (patch: Partial<AxisConfiguration>) => void;
    onShowTopPreviewPauseChange?: (paused: boolean) => void;
};

const AxisConfigurationModalConfigurationControls = ({
    config,
    controlsMeta,
    totalCount,
    valueSalesPercent,
    volumeSalesPercent,
    isPreviewLoading = false,
    hideGroupRestAsOther = false,
    hideShowTopStats = false,
    onChange,
    onShowTopPreviewPauseChange,
}: AxisConfigurationModalConfigurationControlsProps) => {
    const canGroupRest = config.showTop < totalCount;
    const {
        showTopInputError,
        showTopInputValue,
        handleShowTopInputChange,
        handleShowTopInputBlur,
        updateShowTopFromSlider,
        showTopMin,
    } = useAxisShowTopInput({
        totalCount,
        config,
        onChange,
    });

    useEffect(() => {
        onShowTopPreviewPauseChange?.(showTopInputError);
    }, [onShowTopPreviewPauseChange, showTopInputError]);

    const selectedSortOption = useMemo(
        () =>
            controlsMeta.sortBy.options.find((option) => option.id === config.sortBy) ??
            controlsMeta.sortBy.options[0],
        [config.sortBy, controlsMeta.sortBy.options],
    );

    const isShowTopLocked = totalCount <= showTopMin;
    const sliderMin = isShowTopLocked ? Math.max(0, totalCount - 1) : showTopMin;

    const showTopStatsLine = useMemo(
        () =>
            formatShowTopStatsLine(
                config.showTop,
                totalCount,
                valueSalesPercent,
                volumeSalesPercent,
            ),
        [config.showTop, totalCount, valueSalesPercent, volumeSalesPercent],
    );

    return (
        <div className="flex h-[322px] w-[476px] shrink-0 flex-col gap-6 border-r border-backgroundLightGrey bg-white p-6">
            <ConfigFieldRow label={controlsMeta.sortBy.label}>
                <Radio.Group
                    value={config.sortBy}
                    onChange={(event) => {
                        const nextSortBy = event.target.value;
                        const nextSortOption =
                            controlsMeta.sortBy.options.find((option) => option.id === nextSortBy) ??
                            selectedSortOption;
                        const nextSortOrder =
                            nextSortOption?.orderOptions.find((option) => option.defaultSelected)?.id ??
                            nextSortOption?.orderOptions[0]?.id ??
                            'asc';

                        onChange({ sortBy: nextSortBy, sortOrder: nextSortOrder });
                    }}
                    className="!flex !flex-col !gap-1 [&>.ant-radio-wrapper]:!m-0 [&>.ant-radio-wrapper]:!text-sm [&>.ant-radio-wrapper]:!leading-6 [&>.ant-radio-wrapper]:!text-blackAlt"
                >
                    {controlsMeta.sortBy.options.map((option) => (
                        <Radio
                            key={option.id}
                            value={option.id}
                            data-testid={ppaAxisConfigSortByTestId(option.id)}
                        >
                            {option.label}
                        </Radio>
                    ))}
                </Radio.Group>
            </ConfigFieldRow>

            <ConfigFieldRow label="Order">
                <OrderToggle
                    orderOptions={selectedSortOption?.orderOptions ?? []}
                    value={config.sortOrder}
                    onChange={(sortOrder) => onChange({ sortOrder })}
                />
            </ConfigFieldRow>

            {controlsMeta.showTop ? (
                <>
                    <SectionDivider />

                    <ConfigFieldRow label={controlsMeta.showTop.label}>
                        <div>
                            <div className="flex items-center gap-3">
                                <Slider
                                    className="axis-show-top-slider !m-0 flex-1"
                                    min={sliderMin}
                                    max={totalCount}
                                    step={controlsMeta.showTop.step}
                                    value={config.showTop}
                                    disabled={isShowTopLocked}
                                    onChange={updateShowTopFromSlider}
                                />
                                <InputNumber
                                    size="small"
                                    controls={false}
                                    className={`axis-show-top-input !h-6 !w-[49px] !rounded-lg [&_.ant-input-number-input-wrap]:!h-6 [&_.ant-input-number-input]:!h-6 [&_.ant-input-number-input]:!px-[7px] [&_.ant-input-number-input]:!text-center ${
                                        showTopInputError ? 'axis-show-top-input-error' : ''
                                    }`}
                                    {...KPI_INTEGER_INPUT_PROPS}
                                    step={controlsMeta.showTop.step}
                                    value={showTopInputValue}
                                    disabled={isShowTopLocked}
                                    onKeyDown={handleKpiIntegerInputKeyDown}
                                    onChange={(value) => handleShowTopInputChange(parseKpiIntegerInput(value))}
                                    onBlur={handleShowTopInputBlur}
                                    data-testid={PPA_AXIS_CONFIG_SHOW_TOP_INPUT}
                                />
                            </div>
                            {hideShowTopStats ? null : isPreviewLoading ? (
                                <p className="mb-0 mt-1 text-xs leading-4 text-toolbarLabelGrey">Loading...</p>
                            ) : (
                                <p className="mb-0 mt-1 text-xs leading-4 text-toolbarLabelGrey">
                                    {showTopStatsLine}
                                </p>
                            )}
                            {hideGroupRestAsOther ? null : (
                                <Checkbox
                                    className="axis-group-rest-checkbox !mt-6"
                                    checked={config.groupRestAsOther}
                                    disabled={!canGroupRest}
                                    onChange={(event) => onChange({ groupRestAsOther: event.target.checked })}
                                    data-testid={PPA_AXIS_CONFIG_GROUP_REST_CHECKBOX}
                                >
                                    {controlsMeta.showTop.consolidateAsOthers.label}
                                </Checkbox>
                            )}
                        </div>
                    </ConfigFieldRow>
                </>
            ) : null}
        </div>
    );
};

export default AxisConfigurationModalConfigurationControls;
